import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Collections as CollectionsIcon,
  Search as SearchIcon,
  CurrencyExchange as CurrencyIcon,
  CloudSync as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  LocalShipping as PreorderIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

function LandingPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Geolocation-based language detection
  React.useEffect(() => {
    const detectLanguageFromLocation = async () => {
      try {
        // First try IP-based geolocation (more reliable, no permission needed)
        const ipResponse = await fetch('https://api.bigdatacloud.net/data/ip-geolocation-full?key=free');
        if (ipResponse.ok) {
          const ipData = await ipResponse.json();
          const countryCode = ipData.country?.isoAlpha2;
          
          if (countryCode) {
            const countryLanguageMap = {
              'TH': 'th', // Thailand -> Thai
              'CN': 'zh', // China -> Chinese
              'JP': 'ja', // Japan -> Japanese
              // Add more countries as needed
            };

            const detectedLanguage = countryLanguageMap[countryCode];
            
            if (detectedLanguage && detectedLanguage !== currentLanguage) {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), setting language to: ${detectedLanguage}`);
              changeLanguage(detectedLanguage);
              return;
            } else if (detectedLanguage) {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), language already set to: ${detectedLanguage}`);
              return;
            } else {
              console.log(`IP detected country: ${ipData.country?.name} (${countryCode}), using default language: en`);
              return;
            }
          }
        }

        // Fallback to browser geolocation if IP method fails
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000 // 5 minutes cache
            });
          });

          const { latitude, longitude } = position.coords;

          // Use reverse geocoding to get country
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (response.ok) {
            const data = await response.json();
            const countryCode = data.countryCode;

            const countryLanguageMap = {
              'TH': 'th', // Thailand -> Thai
              'CN': 'zh', // China -> Chinese
              'JP': 'ja', // Japan -> Japanese
            };

            const detectedLanguage = countryLanguageMap[countryCode];
            
            if (detectedLanguage && detectedLanguage !== currentLanguage) {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), setting language to: ${detectedLanguage}`);
              changeLanguage(detectedLanguage);
            } else if (detectedLanguage) {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), language already set to: ${detectedLanguage}`);
            } else {
              console.log(`GPS detected country: ${data.countryName} (${countryCode}), using default language: en`);
            }
          }
        }
      } catch (error) {
        console.log('Error detecting location or setting language:', error.message);
        // Fallback to default language (English)
      }
    };

    // Only run geolocation detection if no language is set in localStorage
    const savedLanguage = localStorage.getItem('toyport_language');
    if (!savedLanguage) {
      detectLanguageFromLocation();
    }
  }, [currentLanguage, changeLanguage]);

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (lang) => {
    changeLanguage(lang);
    handleLanguageClose();
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'th', name: 'ไทย', flag: '🇹🇭' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const exampleToys = [
    {
      name: 'RG RX-78-2 Gundam',
      brand: 'Bandai',
      type: 'Gunpla',
      scale: '1/144',
      price: 3500,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=RG+Gundam'
    },
    {
      name: 'Figuarts Zero Luffy',
      brand: 'Bandai',
      type: 'Figure',
      scale: '1/8',
      price: 2800,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=Figuarts+Luffy'
    },
    {
      name: 'Tamiya Tiger I',
      brand: 'Tamiya',
      type: 'Military',
      scale: '1/35',
      price: 4200,
      imageUrl: 'https://placehold.co/300x200/e0e0e0/666666?text=Tamiya+Tiger'
    }
  ];

  const features = [
    {
      icon: <CollectionsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.completeManagement.title'),
      description: t('landing.features.completeManagement.description')
    },
    {
      icon: <SearchIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.smartSearch.title'),
      description: t('landing.features.smartSearch.description')
    },
    {
      icon: <CurrencyIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.multiCurrency.title'),
      description: t('landing.features.multiCurrency.description')
    },
    {
      icon: <PreorderIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.preorderTracking.title'),
      description: t('landing.features.preorderTracking.description')
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.cloudSync.title'),
      description: t('landing.features.cloudSync.description')
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: t('landing.features.secure.title'),
      description: t('landing.features.secure.description')
    }
  ];

  const benefits = [
    t('landing.benefits.neverLose'),
    t('landing.benefits.trackValue'),
    t('landing.benefits.planPurchases'),
    t('landing.benefits.shareCollection'),
    t('landing.benefits.mobileFriendly'),
    t('landing.benefits.freeToUse')
  ];

  const howItWorks = [
    {
      step: 1,
      title: t('landing.howItWorks.signUp.title'),
      description: t('landing.howItWorks.signUp.description'),
      icon: <StarIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 2,
      title: t('landing.howItWorks.addToys.title'),
      description: t('landing.howItWorks.addToys.description'),
      icon: <AddIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 3,
      title: t('landing.howItWorks.organize.title'),
      description: t('landing.howItWorks.organize.description'),
      icon: <ViewListIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    },
    {
      step: 4,
      title: t('landing.howItWorks.monitor.title'),
      description: t('landing.howItWorks.monitor.description'),
      icon: <SpeedIcon sx={{ fontSize: 30, color: 'primary.main' }} />
    }
  ];

  const handleGetStarted = () => {
    if (currentUser) {
      navigate('/toys');
    } else {
      navigate('/login');
    }
  };

  const handleSignUp = () => {
    if (currentUser) {
      navigate('/toys');
    } else {
      navigate('/signup');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Language Switcher AppBar */}
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'flex-end' }}>
          <IconButton
            onClick={handleLanguageClick}
            sx={{ color: 'text.primary' }}
            title="Change Language"
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageClose}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                selected={currentLanguage === lang.code}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box sx={{
        color: 'white',
        py: { xs: 8, md: 12 },
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: 'url(/banner.jfif)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(120deg, rgba(15, 23, 42, 0.75) 0%, rgba(99, 102, 241, 0.78) 55%, rgba(139, 92, 246, 0.55) 100%)'
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2
                }}
              >
                {t('landing.title')}
              </Typography>
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  mb: 3
                }}
              >
                {t('landing.subtitle')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.85,
                  mb: 4,
                  lineHeight: 1.6,
                  maxWidth: 520
                }}
              >
                {t('landing.description')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                >
                  {currentUser ? t('landing.goToCollection') : t('landing.getStarted')}
                </Button>
                {!currentUser && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleSignUp}
                    sx={{
                      borderColor: 'white',
                      color: 'white',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    {t('landing.signUp')}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 0 } }}>
                <Paper
                  elevation={12}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(6px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    color: 'text.primary',
                    maxWidth: 420,
                    width: '100%'
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', color: 'primary.main' }}>
                    {t('landing.sampleCollection')}
                  </Typography>
                  <Grid container spacing={2}>
                    {exampleToys.map((toy, index) => (
                      <Grid item xs={12} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(99, 102, 241, 0.06)' }}>
                          <Avatar
                            variant="rounded"
                            sx={{
                              bgcolor: 'white',
                              border: '1px solid rgba(99, 102, 241, 0.2)',
                              mr: 2,
                              width: 56,
                              height: 56,
                              overflow: 'hidden'
                            }}
                          >
                            <img
                              src={toy.imageUrl}
                              alt={toy.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                              {toy.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {toy.brand} • {toy.type} • {toy.scale}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            ¥{toy.price.toLocaleString()}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h3"
            component="h2"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            {t('landing.features.title')}
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            {t('landing.features.subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 3,
                  textAlign: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.howItWorks.title')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto' }}
            >
              {t('landing.howItWorks.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {howItWorks.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box textAlign="center">
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    mb: 2,
                    position: 'relative'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {step.step}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.benefits.title')}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 4, lineHeight: 1.6 }}
            >
              {t('landing.benefits.subtitle')}
            </Typography>
            <List>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckIcon sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={benefit}
                    primaryTypographyProps={{
                      variant: 'body1',
                      sx: { fontWeight: 500 }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: 'white'
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {t('landing.collectorTypes.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                <Chip label="Gunpla" color="primary" />
                <Chip label="Scale Models" color="primary" />
                <Chip label="Action Figures" color="primary" />
                <Chip label="Dolls" color="primary" />
                <Chip label="Diecast" color="primary" />
                <Chip label="Plastic Kits" color="primary" />
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('landing.collectorTypes.description')}
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                startIcon={<PlayIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                {t('landing.collectorTypes.startCollection')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        py: { xs: 6, md: 8 }
      }}>
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography
              variant="h3"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 700 }}
            >
              {t('landing.cta.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 4,
                lineHeight: 1.6
              }}
            >
              {t('landing.cta.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                {currentUser ? t('landing.goToCollection') : t('landing.getStarted')}
              </Button>
              {!currentUser && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSignUp}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {t('landing.cta.createAccount')}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage; 
